import { Collapse, Drawer, Empty, Switch, Tag, Tooltip } from 'antd'
import { stripArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import type {
  FieldValue,
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { stripComponents } from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import type { FC } from 'react'
import { memo, useMemo, useState } from 'react'
import styled from 'styled-components'

import { classifyLlmField, type LlmFieldComparison } from './classifyLlmField'
import { collectStepFieldIds } from './collectStepFieldIds'
import { diffTokens } from './diffTokens'
import { formatFieldValue, MISSING_VALUE_PLACEHOLDER } from './formatFieldValue'

interface DiffRow {
  fieldId: string
  field: OntologyField | undefined
  userFv: FieldValue | undefined
  llmFv: FieldValue | undefined
  // Pre-formatted display strings for both sides. Computed once at build
  // time so `classifyLlmField` and `DiffRowView` share the result; the
  // alternative was running `formatFieldValue` four times per field per
  // drawer render, which is non-trivial when values serialise to indented
  // JSON.
  userText: string
  llmText: string
  status: LlmFieldComparison
}

interface StepGroup {
  key: string
  title: string
  rows: DiffRow[]
}

interface Props {
  onClose: () => void
}

export const LlmDiffDrawer: FC<Props> = ({ onClose }) => {
  const { fieldValues } = useTicketAccess()
  const { ontologyFields } = useTicketOntologyFields()
  const workflow = useWorkflow()

  const [showMatching, setShowMatching] = useState(false)

  // The diff drawer is ticket-scope (component-scoped rows are a deferred
  // feature) -- strip the reserved `components` subtree so it can't surface
  // as a bogus "Other fields" row.
  const groups = useMemo<StepGroup[]>(
    () =>
      buildStepGroups(
        workflow.steps,
        stripComponents(fieldValues),
        ontologyFields
      ),
    [workflow.steps, fieldValues, ontologyFields]
  )

  const summary = useMemo(() => summarise(groups), [groups])

  const visibleGroups = useMemo(() => {
    if (showMatching) return groups
    return groups
      .map((g) => ({
        ...g,
        // Original behavior hid both present-and-equal rows ('match') and
        // both-absent rows (also 'match' in the old vocabulary). Preserve
        // that by filtering out both of the new equivalents.
        rows: g.rows.filter(
          (r) => r.status !== 'accepted' && r.status !== 'both-missing'
        ),
      }))
      .filter((g) => g.rows.length > 0)
  }, [groups, showMatching])

  return (
    <Drawer
      title='LLM vs User Diff'
      placement='right'
      width={720}
      onClose={onClose}
      open
      styles={{ body: { padding: '1.25rem 1.5rem', overflowY: 'auto' } }}>
      <SummaryRow>
        <SummaryChip $tone='edited'>
          <ChipValue>{summary.edited}</ChipValue>
          <ChipLabel>Differs</ChipLabel>
        </SummaryChip>
        <SummaryChip $tone='userOnly'>
          <ChipValue>{summary.userOnly}</ChipValue>
          <ChipLabel>User only</ChipLabel>
        </SummaryChip>
        <SummaryChip $tone='rejected'>
          <ChipValue>{summary.rejected}</ChipValue>
          <ChipLabel>LLM only</ChipLabel>
        </SummaryChip>
        <SummaryChip $tone='accepted'>
          <ChipValue>{summary.accepted}</ChipValue>
          <ChipLabel>Match</ChipLabel>
        </SummaryChip>
      </SummaryRow>

      <ToggleRow>
        <Switch
          size='small'
          checked={showMatching}
          onChange={setShowMatching}
        />
        <ToggleLabel>Show matching fields</ToggleLabel>
      </ToggleRow>

      {visibleGroups.length === 0 ? (
        <Empty
          description={
            showMatching
              ? 'No fields tracked on this ticket yet'
              : 'No differences found'
          }
        />
      ) : (
        <Collapse
          bordered={false}
          defaultActiveKey={visibleGroups.map((g) => g.key)}
          items={visibleGroups.map((g) => ({
            key: g.key,
            label: (
              <StepHeader>
                <StepTitle>{g.title}</StepTitle>
                <StepCount>
                  {g.rows.length} field{g.rows.length === 1 ? '' : 's'}
                </StepCount>
              </StepHeader>
            ),
            children: (
              <RowList>
                {g.rows.map((row) => (
                  <DiffRowView key={row.fieldId} row={row} />
                ))}
              </RowList>
            ),
          }))}
        />
      )}
    </Drawer>
  )
}

// ---------------------------------------------------------------------------
// Row rendering
// ---------------------------------------------------------------------------

// `row` references are stable across UI-only re-renders because `groups` is
// memoised on `[workflow.steps, fieldValues, ontologyFields]` and
// `visibleGroups` preserves row refs through `.filter()`. Wrapping in
// `memo` cuts the "Show matching fields" toggle from re-rendering every
// card to re-rendering only the ones whose visibility changed.
const DiffRowView = memo(function DiffRowView({ row }: { row: DiffRow }) {
  const label = row.field?.name ?? row.fieldId
  // Flag-category fields don't carry a `type`; only show the tag when present.
  const type =
    row.field && 'type' in row.field ? (row.field.type as string) : undefined

  const { llmSegments, userSegments } = useRowSegments(
    row.status,
    row.llmText,
    row.userText
  )

  const llmTone: CellTone =
    row.status === 'edited' || row.status === 'rejected' ? row.status : 'none'
  const userTone: CellTone =
    row.status === 'edited' || row.status === 'userOnly' ? row.status : 'none'

  return (
    <RowCard>
      <RowHeader>
        <RowLabelGroup>
          <Tooltip title={row.fieldId} placement='topLeft'>
            <RowLabel>{label}</RowLabel>
          </Tooltip>
          {type && <Tag>{type}</Tag>}
        </RowLabelGroup>
        <StatusBadge status={row.status} />
      </RowHeader>
      <ValueGrid>
        <ValueBlock>
          <ValueHeader>LLM</ValueHeader>
          <ValueBody $missing={row.llmFv === undefined} $tone={llmTone}>
            {llmSegments ? (
              <SegmentedText segments={llmSegments} side='llm' />
            ) : (
              row.llmText
            )}
          </ValueBody>
        </ValueBlock>
        <ValueBlock>
          <ValueHeader>User</ValueHeader>
          <ValueBody $missing={row.userFv === undefined} $tone={userTone}>
            {userSegments ? (
              <SegmentedText segments={userSegments} side='user' />
            ) : (
              row.userText
            )}
          </ValueBody>
        </ValueBlock>
      </ValueGrid>
    </RowCard>
  )
})

type CellTone = 'none' | 'edited' | 'userOnly' | 'rejected'

type Segment = { text: string; kind: 'same' | 'unique' }

function useRowSegments(
  status: LlmFieldComparison,
  llmText: string,
  userText: string
): { llmSegments: Segment[] | null; userSegments: Segment[] | null } {
  return useMemo(() => {
    if (
      status !== 'edited' ||
      llmText === MISSING_VALUE_PLACEHOLDER ||
      userText === MISSING_VALUE_PLACEHOLDER
    ) {
      return { llmSegments: null, userSegments: null }
    }
    const { llm, user } = diffTokens(llmText, userText)
    return { llmSegments: llm, userSegments: user }
  }, [status, llmText, userText])
}

const SegmentedText: FC<{ segments: Segment[]; side: 'llm' | 'user' }> = ({
  segments,
  side,
}) => (
  <>
    {segments.map((seg, i) =>
      seg.kind === 'unique' ? (
        <TokenMark key={i} $side={side}>
          {seg.text}
        </TokenMark>
      ) : (
        <span key={i}>{seg.text}</span>
      )
    )}
  </>
)

const STATUS_META: Record<
  LlmFieldComparison,
  { label: string; color: string }
> = {
  accepted: { label: 'Match', color: 'green' },
  edited: { label: 'Differs', color: 'orange' },
  userOnly: { label: 'User only', color: 'blue' },
  rejected: { label: 'LLM only', color: 'purple' },
  // Both sides empty — distinct from `accepted` so reviewers can tell
  // "nothing to compare" apart from "the LLM and user agreed". Matches
  // the `bothMissing` tag in the Ticket Metrics breakdown.
  'both-missing': { label: 'Empty', color: 'default' },
}

const StatusBadge: FC<{ status: LlmFieldComparison }> = ({ status }) => {
  const meta = STATUS_META[status]
  return <Tag color={meta.color}>{meta.label}</Tag>
}

// ---------------------------------------------------------------------------
// Diff computation
// ---------------------------------------------------------------------------

const OTHER_GROUP_KEY = '__other__'

function buildStepGroups(
  steps: ReadonlyArray<LibraWorkflowStep>,
  fieldValues: FieldValues,
  ontologyFields: Record<string, OntologyField>
): StepGroup[] {
  const seen = new Set<string>()
  const groups: StepGroup[] = []

  for (const step of steps) {
    const ids = collectStepFieldIds(step)
    if (ids.length === 0) continue

    const rows: DiffRow[] = []
    for (const id of ids) {
      if (seen.has(id)) continue
      seen.add(id)
      rows.push(buildRow(id, fieldValues, ontologyFields))
    }

    if (rows.length === 0) continue
    groups.push({ key: step.id, title: step.name, rows })
  }

  const unmapped = Object.keys(fieldValues).filter((id) => !seen.has(id))

  if (unmapped.length > 0) {
    const rows = unmapped
      .map((id) => buildRow(id, fieldValues, ontologyFields))
      .sort((a, b) =>
        (a.field?.name ?? a.fieldId).localeCompare(b.field?.name ?? b.fieldId)
      )
    groups.push({ key: OTHER_GROUP_KEY, title: 'Other fields', rows })
  }

  return groups
}

export function buildRow(
  fieldId: string,
  fieldValues: FieldValues,
  ontologyFields: Record<string, OntologyField>
): DiffRow {
  const rawUser = fieldValues[fieldId]
  // Strip locally stamped `_id`s from array-of-object rows on both sides so
  // the diff tracker never flags rows as different purely because the user
  // side carries a row-identity token that `llmValue` (immutable lineage)
  // does not. See `stampArrayRowIds` for where the `_id`s are introduced.
  const userFv: FieldValue | undefined = rawUser
    ? { ...rawUser, value: stripArrayRowIds(rawUser.value) }
    : undefined
  // LLM side is the inline `llmValue` sibling on the same FieldValue. We
  // synthesise a `FieldValue`-shaped wrapper so downstream formatting /
  // classification can treat both sides uniformly.
  const llmFv: FieldValue | undefined =
    rawUser && rawUser.llmValue !== undefined
      ? { value: stripArrayRowIds(rawUser.llmValue), source: rawUser.source }
      : undefined

  const userText = formatFieldValue(userFv)
  const llmText = formatFieldValue(llmFv)

  return {
    fieldId,
    field: ontologyFields[fieldId],
    userFv,
    llmFv,
    userText,
    llmText,
    status: classifyLlmField({ userFv, userText, llmFv, llmText }),
  }
}

function summarise(groups: StepGroup[]): {
  accepted: number
  edited: number
  userOnly: number
  rejected: number
} {
  const out = { accepted: 0, edited: 0, userOnly: 0, rejected: 0 }
  for (const g of groups) {
    for (const r of g.rows) {
      if (r.status === 'accepted') out.accepted++
      else if (r.status === 'edited') out.edited++
      else if (r.status === 'userOnly') out.userOnly++
      else if (r.status === 'rejected') out.rejected++
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`

type ChipTone = 'accepted' | 'edited' | 'userOnly' | 'rejected'

const chipTone = (tone: ChipTone): string => {
  switch (tone) {
    case 'accepted':
      return '#52c41a'
    case 'edited':
      return '#faad14'
    case 'userOnly':
      return '#1677ff'
    case 'rejected':
      return '#722ed1'
  }
}

const SummaryChip = styled.div<{ $tone: ChipTone }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.colors.gray[800]};
  border-left: 3px solid ${({ $tone }) => chipTone($tone)};
`

const ChipValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.forecast.activeText};
`

const ChipLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const ToggleLabel = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const StepTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const StepCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RowCard = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  padding: 0.625rem 0.75rem;
  background: ${({ theme }) => theme.colors.gray[800]};
`

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const RowLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`

const RowLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.8125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

const ValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

const ValueHeader = styled.span`
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const TONE_BORDER: Record<CellTone, string> = {
  none: 'transparent',
  edited: '#faad14',
  userOnly: '#1677ff',
  rejected: '#722ed1',
}

const TONE_BG: Record<CellTone, string> = {
  none: 'transparent',
  edited: 'rgba(250, 173, 20, 0.08)',
  userOnly: 'rgba(22, 119, 255, 0.08)',
  rejected: 'rgba(114, 46, 209, 0.08)',
}

const ValueBody = styled.div<{ $missing: boolean; $tone: CellTone }>`
  font-size: 0.8125rem;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  background: ${({ $tone, theme }) =>
    $tone === 'none' ? theme.forecast.background1 : TONE_BG[$tone]};
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === 'none' ? theme.forecast.border : TONE_BORDER[$tone]};
  color: ${({ $missing, theme }) =>
    $missing ? theme.forecast.secondaryText : theme.forecast.activeText};
  font-style: ${({ $missing }) => ($missing ? 'italic' : 'normal')};
  word-break: break-word;
  white-space: pre-wrap;
`

const TokenMark = styled.span<{ $side: 'llm' | 'user' }>`
  background: ${({ $side }) =>
    $side === 'llm' ? 'rgba(255, 77, 79, 0.22)' : 'rgba(82, 196, 26, 0.22)'};
  color: ${({ $side, theme }) =>
    $side === 'llm' ? '#ff7875' : theme.forecast.activeText};
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 600;
`
